import { timeToMinutes, toTwo } from './utils';
import { getNowInSelectedTZParts, localTzToUTC } from './timeZoneHelpers';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';

export interface GeneratedSlot {
  startMin: number;
  endMin: number;
  startTime: string;
  endTime: string;
}

export function generateAvailableSlots(
  startMin: number,
  endMin: number,
  dateStr: string,
  selectedTimeZone: string,
  now: Date,
  slotDuration = 60
): GeneratedSlot[] {
  const slots: GeneratedSlot[] = [];
  const tzNow = getNowInSelectedTZParts(selectedTimeZone);
  const todayStr = tzNow.todayStr;
  const nowMinutes = timeToMinutes(`${tzNow.hour}:${toTwo(tzNow.minute)}`);
  
  const today0 = new Date(now);
  today0.setHours(0, 0, 0, 0);
  const [yyyy, mm, dd] = dateStr.split('-').map(Number);
  const slotDate = new Date(yyyy, mm - 1, dd);
  slotDate.setHours(0, 0, 0, 0);
  const isPastDay = slotDate < today0;

  if (isPastDay && !isImpersonated()) {
    return slots;
  }
  
  // Calcular la hora actual en milisegundos para comparar con 24 horas de anticipación
  const nowMs = now.getTime();
  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
  
  const maxEndMin = Math.min(endMin, 24 * 60);
  
  let current = startMin;
  
  while (current < maxEndMin) {
    const slotEnd = current + slotDuration;
    
    // Don't create slot if it extends beyond the available range
    if (slotEnd > maxEndMin) {
      break;
    }
    
    if (!isImpersonated()) {
      if (dateStr === todayStr && slotEnd <= nowMinutes) {
        current = slotEnd;
        continue;
      }

      if (dateStr === todayStr && current < nowMinutes) {
        current = slotEnd;
        continue;
      }
    }
    
    // Validar que el slot tenga al menos 24 horas de anticipación
    const slotHour = Math.floor(current / 60);
    const slotMinute = current % 60;
    const slotTimeStr = `${toTwo(slotHour)}:${toTwo(slotMinute)}`;
    
    // Convertir la fecha/hora del slot (en la zona horaria del psicólogo) a UTC
    const slotDateTimeUTC = localTzToUTC(dateStr, slotTimeStr, selectedTimeZone);
    const slotMs = slotDateTimeUTC.getTime();
    
    // Calcular la diferencia en milisegundos
    const timeDiffMs = slotMs - nowMs;
    
    // Si el slot está a menos de 24 horas, no incluirlo (excepto impersonación)
    if (timeDiffMs < TWENTY_FOUR_HOURS_MS && !isImpersonated()) {
      current = slotEnd;
      continue;
    }
    
    const endTimeStr = slotEnd === 1440 ? '00:00' : `${toTwo(Math.floor(slotEnd / 60))}:${toTwo(slotEnd % 60)}`;
    
    slots.push({
      startMin: current,
      endMin: slotEnd,
      startTime: `${toTwo(Math.floor(current / 60))}:${toTwo(current % 60)}`,
      endTime: endTimeStr
    });
    current = slotEnd;
  }
  
  return slots;
}

